/**
 * WalkthroughViewer — in-app, tier-gated viewer for a module's runnable
 * worked-solution file.
 *
 * Replaces the old "View full walkthrough" link to the public GitHub repo
 * (which 404'd before merge and exposed solutions publicly). The source is
 * fetched from GET /modules/{slug}/walkthrough — gated server-side — and shown
 * in a collapsible dark code box, so full solutions stay inside the paid
 * product.
 *
 * Collapsed by default; the source is fetched only when first expanded
 * (and cached across every card on the page via the shared query key).
 */

import React, { useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CodeIcon from "@mui/icons-material/Code";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getModuleWalkthrough, ModuleWalkthrough } from "../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export default function WalkthroughViewer({ slug }: { slug: string }) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery<ModuleWalkthrough | null>({
    queryKey: ["walkthrough", slug],
    queryFn: () => getModuleWalkthrough(slug),
    enabled: open, // fetch only once the reader opens it
    retry: false,
  });

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((o) => !o)}
        startIcon={<CodeIcon sx={{ fontSize: "1rem" }} />}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 500,
          color: theme.palette.primary.main,
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}
      >
        {open ? t("WalkthroughViewer.hide") : t("WalkthroughViewer.viewFull")}
      </Button>

      {open && (
        <Box sx={{ mt: 1 }}>
          {isLoading && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
              <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
              <Typography variant="caption" color="text.secondary">
                {t("WalkthroughViewer.loading")}
              </Typography>
            </Stack>
          )}

          {!isLoading && !data && (
            <Typography variant="caption" color="text.secondary">
              {t("WalkthroughViewer.unavailable")}
            </Typography>
          )}

          {!isLoading && data && (
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: "8px 8px 0 0",
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderBottom: "none",
                }}
              >
                <Typography
                  sx={{ fontFamily: MONO, fontSize: "0.74rem", color: theme.palette.text.disabled }}
                >
                  {data.filename}
                </Typography>
                <Typography
                  sx={{ fontFamily: MONO, fontSize: "0.7rem", color: theme.palette.text.disabled }}
                >
                  {t("WalkthroughViewer.runCommand", { filename: data.filename })}
                </Typography>
              </Stack>
              {/* A <div> (not <pre>) with explicit colors so the lesson-markdown
                  `& pre` styling can never bleed onto it. */}
              <Box
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.disabled,
                  border: "1px solid rgba(255,255,255,0.10)",
                  borderRadius: "0 0 8px 8px",
                  fontFamily: MONO,
                  fontSize: "0.78rem",
                  lineHeight: 1.6,
                  whiteSpace: "pre",
                  overflow: "auto",
                  maxHeight: 460,
                }}
              >
                {data.source}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
